/**
 * TodayBriefSections — the family brief on Today.
 * Presentation only. Every checkbox writes to the original record via
 * handlers passed down from TodayHousePage (one source of truth).
 */
import { Caption } from "./os/editorial";

export function fmtClock(t) {
  if (!t || typeof t !== "string") return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
}

/* Quiet circular checkbox — hairline, no fill. */
export function QuietCheck({ onCheck, testid, label }) {
  return (
    <button
      type="button"
      onClick={onCheck}
      aria-label={label || "Mark complete"}
      data-testid={testid}
      className="mt-[3px] w-[18px] h-[18px] rounded-full flex-shrink-0 transition-ara hover:opacity-50"
      style={{ border: "1px solid var(--ara-line)", background: "transparent" }}
    />
  );
}

/* ── Today's schedule — compact snapshot, only hours that hold events ── */

export function TodaySchedule({ events, members, birthdayNote }) {
  const realMembers = (members || []).filter(m => m.ageGroup !== "Pet");
  const idSet = new Set(realMembers.map(m => m.id));
  const active = (events || [])
    .filter(e => e.status !== "done" && e.startTime)
    .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
  if (active.length === 0 && !birthdayNote) return null;

  const isShared = (e) => e.memberId === "all" || e.memberId === "everyone" || !idSet.has(e.memberId);
  const shared = active.filter(isShared);
  const cols = realMembers.filter(m => active.some(e => e.memberId === m.id));
  const hasFamilyCol = shared.length > 0;

  const hours = [...new Set(active.map(e => Number((e.startTime || "0").split(":")[0])))].sort((a, b) => a - b);
  const firstName = (m) => (m.preferredName || m.name || "").trim().split(" ")[0];
  const nameFor = (e) => {
    if (isShared(e)) return "Whole family";
    return firstName(realMembers.find(m => m.id === e.memberId) || {}) || "Family";
  };
  const eventsFor = (memberId, hour) => active.filter(e =>
    !isShared(e) && e.memberId === memberId && Number(e.startTime.split(":")[0]) === hour);
  const sharedFor = (hour) => shared.filter(e => Number(e.startTime.split(":")[0]) === hour);
  const colCount = cols.length + (hasFamilyCol ? 1 : 0);

  return (
    <div data-testid="today-schedule-body">
      {birthdayNote && (
        <p className="mb-4 prose-ara text-[14px] italic" style={{ color: "var(--ara-ink-muted)" }} data-testid="today-schedule-birthday">
          {birthdayNote}
        </p>
      )}

      {active.length > 0 && (
        <>
          {/* Desktop / tablet — TIME column + one hairline column per member */}
          <div className="hidden md:block" data-testid="today-schedule-desktop">
            <div className="grid" style={{ gridTemplateColumns: `64px repeat(${colCount}, minmax(0, 1fr))` }}>
              <div className="pb-2 border-b" style={{ borderColor: "var(--ara-line-soft)" }}>
                <Caption>Time</Caption>
              </div>
              {cols.map(m => (
                <div key={m.id} className="pb-2 pl-4 border-b" style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`today-schedule-col-${m.id}`}>
                  <Caption>{firstName(m)}</Caption>
                </div>
              ))}
              {hasFamilyCol && (
                <div className="pb-2 pl-4 border-b" style={{ borderColor: "var(--ara-line-soft)" }} data-testid="today-schedule-col-family">
                  <Caption>Whole family</Caption>
                </div>
              )}
              {hours.map(hour => (
                <ScheduleRow
                  key={hour}
                  hour={hour}
                  cols={cols}
                  hasFamilyCol={hasFamilyCol}
                  eventsFor={eventsFor}
                  sharedEvents={sharedFor(hour)}
                />
              ))}
            </div>
          </div>

          {/* Mobile — chronological list, no horizontal scroll */}
          <div className="md:hidden" data-testid="today-schedule-mobile">
            {active.map((e, i) => (
              <div
                key={e.id}
                className={`flex items-baseline gap-4 py-3 ${i === active.length - 1 ? "" : "border-b"}`}
                style={{ borderColor: "var(--ara-line-soft)" }}
                data-testid={`today-schedule-mobile-event-${e.id}`}
              >
                <p className="w-[72px] flex-shrink-0 text-[13px] tabular-nums" style={{ color: "var(--ara-ink-soft)", fontFamily: "Inter, system-ui, sans-serif" }}>
                  {fmtClock(e.startTime)}
                </p>
                <div className="min-w-0">
                  <p className="text-[14.5px] leading-snug" style={{ color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 }}>
                    {nameFor(e)} — {e.title}
                  </p>
                  {e.location && <p className="mt-0.5 caption-ara" style={{ color: "var(--ara-ink-soft)" }}>{e.location}</p>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ScheduleRow({ hour, cols, hasFamilyCol, eventsFor, sharedEvents }) {
  const hourLabel = fmtClock(`${String(hour).padStart(2, "0")}:00`).replace(":00", "");
  return (
    <>
      <div className="py-2.5 border-b" style={{ borderColor: "var(--ara-line-soft)" }} data-testid={`today-schedule-row-${hour}`}>
        <p className="text-[12px] tabular-nums" style={{ color: "var(--ara-ink-soft)", fontFamily: "Inter, system-ui, sans-serif" }}>{hourLabel}</p>
      </div>
      {cols.map(m => (
        <div key={m.id} className="py-2.5 pl-4 border-b border-l" style={{ borderColor: "var(--ara-line-soft)" }}>
          {eventsFor(m.id, hour).map(e => <ScheduleEntry key={e.id} event={e} />)}
        </div>
      ))}
      {hasFamilyCol && (
        <div className="py-2.5 pl-4 border-b border-l" style={{ borderColor: "var(--ara-line-soft)" }}>
          {sharedEvents.map(e => <ScheduleEntry key={e.id} event={e} />)}
        </div>
      )}
    </>
  );
}

function ScheduleEntry({ event }) {
  return (
    <div className="mb-1 last:mb-0" data-testid={`today-schedule-event-${event.id}`}>
      <p className="text-[13px] leading-snug" style={{ color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 }}>
        <span className="tabular-nums" style={{ color: "var(--ara-ink-soft)", fontWeight: 400 }}>{fmtClock(event.startTime)}</span>
        {"  "}{event.title}
      </p>
      {event.location && <p className="caption-ara mt-0.5" style={{ color: "var(--ara-ink-soft)" }}>{event.location}</p>}
    </div>
  );
}

/* ── Today's chores — grouped by member, checkbox completes the task ── */

export function TodayChores({ groups, onComplete }) {
  return (
    <div data-testid="today-chores-body">
      {groups.map((g, gi) => (
        <div key={g.key} className={gi === 0 ? "" : "mt-6"} data-testid={`today-chores-group-${g.key}`}>
          <Caption>{g.name}</Caption>
          <div className="mt-2">
            {g.chores.map((t, i) => (
              <div
                key={t.id}
                className={`flex items-start gap-4 py-3 ${i === g.chores.length - 1 ? "" : "border-b"}`}
                style={{ borderColor: "var(--ara-line-soft)" }}
                data-testid={`today-chore-${t.id}`}
              >
                <QuietCheck onCheck={() => onComplete(t, g)} testid={`today-chore-check-${t.id}`} label={`Mark ${t.title} done`} />
                <div className="min-w-0">
                  <p className="text-[14.5px] leading-snug" style={{ color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 }}>
                    {t.title}
                  </p>
                  {t.dueLabel && <p className="mt-0.5 caption-ara" style={{ color: "var(--ara-ink-soft)" }}>{t.dueLabel}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Shopping reminders — active items only ── */

export function TodayShopping({ items, remaining, onComplete }) {
  return (
    <div data-testid="today-shopping-body">
      {items.map((it, i) => (
        <div
          key={it.id}
          className={`flex items-start gap-4 py-3 ${i === items.length - 1 ? "" : "border-b"}`}
          style={{ borderColor: "var(--ara-line-soft)" }}
          data-testid={`today-shopping-${it.id}`}
        >
          <QuietCheck onCheck={() => onComplete(it)} testid={`today-shopping-check-${it.id}`} label={`Mark ${it.item} bought`} />
          <p className="text-[14.5px] leading-snug" style={{ color: "var(--ara-ink)", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 500 }}>
            {it.item}{it.quantity ? <span style={{ color: "var(--ara-ink-soft)", fontWeight: 400 }}> · {it.quantity}</span> : null}
          </p>
        </div>
      ))}
      {remaining > 0 && (
        <p className="mt-3 caption-ara" style={{ color: "var(--ara-ink-soft)" }} data-testid="today-shopping-remaining">
          {remaining} more on the full list.
        </p>
      )}
    </div>
  );
}
